import  { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FaEye, FaEyeSlash, FaCamera } from "react-icons/fa";
import { FaStarOfLife } from "react-icons/fa6";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from "../../components/shared/Button";
import { loginPath, studentPath } from "../../components/routes/path";
import axios from "axios";
import QiraatCourseSelection from "./Qirahat"; 
import { AVAILABLE_COURSES, QIRAAT_RIWAYAT } from "../../components/Data/datas";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api";



const SignUpForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [isHafiz, setIsHafiz] = useState(false);
  const [location, setLocation] = useState("nigeria");
  const [coursePrice, setCoursePrice] = useState(0);
  const [registrationStep, setRegistrationStep] = useState(1);
  const [otpValue, setOtpValue] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const navigate = useNavigate();
  console.log("Current course price:", otpSending);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      gender: "",
      age: "",
      isHafiz: false,
      location: "nigeria",
      courseId: null,
      riwayatId: null,
      paymentOptionType: null,
    },
  });

  // Watch password for confirmation validation
  const password = watch("password", "");

  // First, let's improve the updatePriceBasedOnSelection function
  const updatePriceBasedOnSelection = useCallback(
    (courseId, paymentOptionType, userLocation) => {
      if (!courseId || !paymentOptionType) {
        return;
      }

      const course = AVAILABLE_COURSES.find((c) => c.id === courseId);
      if (!course) {
        return;
      }

      // If it's a Qira'at course and a riwayatId is selected
      if (course.id === 3 && watch("riwayatId")) {
        try {
          const riwayatId = parseInt(watch("riwayatId"));

          // Make sure we can access the QIRAAT_RIWAYAT data
          if (QIRAAT_RIWAYAT) {
            const riwayat = QIRAAT_RIWAYAT.find((r) => r.id === riwayatId);
            if (riwayat) {
              const paymentOption = riwayat.paymentOptions.find(
                (po) => po.type === paymentOptionType
              );

              if (paymentOption) {
                const locationToUse = userLocation || location;
                const price =
                  locationToUse === "nigeria"
                    ? paymentOption.nigeriaPrice
                    : paymentOption.diasporaPrice;

                setCoursePrice(price);
                return;
              }
            }
          }
        } catch (error) {
          console.error("Error processing riwayat payment options:", error);
        }
      }

      // Handle regular courses
      const paymentOption = course.paymentOptions.find(
        (po) => po.type === paymentOptionType
      );

      if (!paymentOption) {
        return;
      }

      const locationToUse = userLocation || location;
      const price =
        locationToUse === "nigeria"
          ? paymentOption.nigeriaPrice
          : paymentOption.diasporaPrice;

      setCoursePrice(price);
    },
    [watch, location, setCoursePrice]
  );

  // Watch for changes to the hafiz status and location
  useEffect(() => {
    const subscription = watch((value) => {
      if (value.isHafiz !== undefined) {
        setIsHafiz(value.isHafiz);

        // If user is no longer a Hafiz but had selected Qira'at, reset selection
        if (!value.isHafiz && selectedCourse === 3) {
          setSelectedCourse(null);
          setSelectedPaymentOption(null);
          setValue("courseId", null);
          setValue("paymentOptionType", null);
          setCoursePrice(0);

          // Also reset riwayatId if it was set
          if (value.riwayatId) {
            setValue("riwayatId", null);
          }
        }
      }

      if (value.location !== undefined) {
        setLocation(value.location);

        // Update price based on new location if course and payment option are selected
        if (selectedCourse && selectedPaymentOption) {
          updatePriceBasedOnSelection(
            selectedCourse,
            selectedPaymentOption,
            value.location
          );
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    watch,
    selectedCourse,
    selectedPaymentOption,
    setValue,
    updatePriceBasedOnSelection,
  ]);

  // Generate MUT student ID on component mount
  useEffect(() => {
    const generateStudentId = async () => {
      try {
        // Generate a temporary ID first, in case the API fails
        const tempId = generateTempStudentId();
        setStudentId(tempId);
        setValue("studentId", tempId);

        // Set up timeout for API call
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // Try to get an ID from the API
        const response = await axios.get(
          `${API_URL}/auth/generate-student-id`,
          {
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (response.data && response.data.studentId) {
          setStudentId(response.data.studentId);
          setValue("studentId", response.data.studentId);
        }
      } catch (error) {
        console.error("Error generating student ID:", error);
        // No need for error toast, we already have a fallback ID
      }
    };

    // Helper function to generate a fallback student ID
    const generateTempStudentId = () => {
      const date = new Date();
      const year = date.getFullYear().toString().substr(-2);
      const randomNum = Math.floor(10000 + Math.random() * 90000);
      return `MUT${year}${randomNum}`;
    };

    generateStudentId();
  }, [setValue]);

  // Handle profile image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid image file (JPEG, PNG)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setProfileImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle hafiz status change
  const handleHafizStatusChange = (e) => {
    setIsHafiz(e.target.checked);
    setValue("isHafiz", e.target.checked);

    // If user was selecting Qira'at but is no longer a Hafiz, reset selection
    if (!e.target.checked && selectedCourse === 3) {
      setSelectedCourse(null);
      setSelectedPaymentOption(null);
      setValue("courseId", null);
      setValue("paymentOptionType", null);
      setValue("riwayatId", null); // Also reset riwayatId
      setCoursePrice(0);
    }
  };

  // Handle location change
  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    setValue("location", e.target.value);

    // Update price based on new location if course and payment option are selected
    if (selectedCourse && selectedPaymentOption) {
      updatePriceBasedOnSelection(
        selectedCourse,
        selectedPaymentOption,
        e.target.value
      );
    }
  };

  // Then let's make sure the payment selection updates the price
  const handleCourseSelect = (courseId) => {
    const course = AVAILABLE_COURSES.find((c) => c.id === courseId);
    if (!course) return;

    // Check if course requires Hafiz status
    if (course.requiresHafiz && !isHafiz) {
      toast.error("Qira'at courses are only available for Hafiz students");
      return;
    }
    setSelectedCourse(courseId);
    setValue("courseId", courseId);

    // Reset payment option
    setSelectedPaymentOption(null);
    setValue("paymentOptionType", null);
    setCoursePrice(0);

    // If course has only one payment option, select it automatically
    if (course.paymentOptions.length === 1) {
      const paymentType = course.paymentOptions[0].type;
      setSelectedPaymentOption(paymentType);
      setValue("paymentOptionType", paymentType);

      // Update price
      updatePriceBasedOnSelection(courseId, paymentType, location);
    }
  };

  // Add a handlePaymentOptionSelect function if you don't have one
  const handlePaymentOptionSelect = (paymentType) => {
    setSelectedPaymentOption(paymentType);
    setValue("paymentOptionType", paymentType);

    // Update price based on selection
    if (selectedCourse) {
      updatePriceBasedOnSelection(selectedCourse, paymentType, location);
    }
  };
  // Handle OTP input change
  const handleOtpChange = (e) => {
    // Only allow numeric input
    const value = e.target.value.replace(/[^0-9]/g, "");
    setOtpValue(value);
  };

  // Handle payment method selection
  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  // Send OTP for verification - Fixed implementation
  const sendVerificationOTP = async (email) => {
    try {
      setOtpSending(true);

      // Create an AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 10000);

      // Log the request for debugging

      try {
        // Actual API call to send OTP
        const response = await axios.post(
          `${API_URL}/auth/send-otp`,
          { email },
          {
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // Clear the timeout since the request completed
        clearTimeout(timeoutId);

        if (response.data && response.data.success) {
          toast.success(`Verification code sent to your email ${email}`);
          return true;
        } else {
          // Handle specific error cases from backend
          const errorMessage =
            response.data?.message || "Failed to send verification code";
          toast.error(errorMessage);
          return false;
        }
      } catch (axiosError) {
        // Clear the timeout in case of error
        clearTimeout(timeoutId);
        throw axiosError; // Re-throw to be handled in outer catch
      }
    } catch (error) {
      console.error("Error sending OTP:", error);

      // Provide better error messages based on the error type
      if (error.name === "AbortError") {
        toast.error(
          "Request timed out. Please check your internet connection and try again."
        );
      } else if (error.code === "ERR_NETWORK") {
        toast.error(
          "Network error. Please check your internet connection and try again."
        );
      } else if (error.response && error.response.status === 429) {
        toast.error("Too many attempts. Please try again later.");
      } else if (error.response && error.response.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          "Failed to send verification code. Please try again later."
        );
      }
      return false;
    } finally {
      setOtpSending(false);
    }
  };

  // Verify OTP
  const verifyOTP = async (email, otp) => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit verification code");
      return false;
    }

    try {
      setIsLoading(true);

      // Improved error handling with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      // API call to verify OTP
      const response = await axios.post(
        `${API_URL}/auth/verify-otp`,
        { email, otp },
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (response.data && response.data.success) {
        toast.success("Email verified successfully");
        return true;
      } else {
        const errorMessage =
          response.data?.message || "Invalid verification code";
        toast.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);

      if (error.name === "AbortError") {
        toast.error("Request timed out. Please try again.");
      } else if (error.response && error.response.status === 429) {
        toast.error("Too many attempts. Please try again later.");
      } else if (error.response && error.response.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Verification failed. Please try again.");
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Process payment
  const processPayment = async (formData) => {
    try {
      setIsLoading(true);

      // Get selected course and payment option details
      const course = AVAILABLE_COURSES.find((c) => c.id === selectedCourse);
      // Determine payment option and price
      let paymentOption;

      if (course.id === 3 && formData.riwayatId) {
        // For Qira'at courses with riwayat selection
        try {
          const riwayatId = parseInt(formData.riwayatId);
          // Make sure we can access the QIRAAT_RIWAYAT data
          if (QIRAAT_RIWAYAT) {
            const riwayat = QIRAAT_RIWAYAT.find((r) => r.id === riwayatId);
            if (riwayat) {
              paymentOption = riwayat.paymentOptions.find(
                (po) => po.type === selectedPaymentOption
              );
            }
          }
        } catch (error) {
          console.error("Error processing riwayat payment options:", error);
        }
      }
      // If no specific riwayat payment option was found, fall back to regular course payment options
      if (!paymentOption) {
        paymentOption = course.paymentOptions.find(
          (po) => po.type === selectedPaymentOption
        );
      }

      if (!paymentOption) {
        throw new Error("Payment option not found");
      }

      // Payment data preparation
      const paymentData = {
        amount: coursePrice,
        currency: location === "nigeria" ? "NGN" : "USD",
        paymentMethod,
        email: formData.email,
        courseId: selectedCourse,
        riwayatId: formData.riwayatId || null, // Include riwayatId if selected
        paymentType: selectedPaymentOption,
        paymentLabel: paymentOption.label,
        description: `${course.name} - ${paymentOption.label} for ${formData.fullName}`,
        studentId: studentId, // Include student ID in the payment data
      };

      // API call with improved error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await axios.post(
        `${API_URL}/payments/process`,
        paymentData,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (response.data && response.data.success) {
        toast.success("Payment processed successfully");
        return response.data.paymentId;
      } else {
        const errorMessage =
          response.data?.message || "Payment failed. Please try again.";
        toast.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error("Payment processing error:", error);

      if (error.name === "AbortError") {
        toast.error("Payment request timed out. Please try again.");
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data?.message || "Invalid payment details");
      } else if (error.response && error.response.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Payment failed. Please try again.");
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Send confirmation email
  const sendConfirmationEmail = async (studentData) => {
    try {
      await axios.post(
        `${API_URL}/notifications/send-confirmation`,
        studentData
      );

      return true;
    } catch (error) {
      console.error("Error sending confirmation email:", error);
      // Don't show an error toast here as it's not critical
      return false;
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      // Check if student ID is available, if not, generate a new one
      if (!studentId) {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const randomNum = Math.floor(10000 + Math.random() * 90000);
        const newStudentId = `MUT${year}${randomNum}`;
        setStudentId(newStudentId);
        setValue("studentId", newStudentId);
      }

      // Step 1: Basic Registration
      if (registrationStep === 1) {
        if (data.courseId === 3 && data.isHafiz) {
          if (!data.riwayatId) {
            toast.error("Please select a riwayat for Qira'at course");
            setIsLoading(false);
            return;
          }
          if (!data.paymentOptionType) {
            toast.error("Please select a payment option for Qira'at course");
            setIsLoading(false);
            return;
          }
        }
        if (!data.courseId || !data.paymentOptionType) {
          toast.error("Please select a course and payment option");
          setIsLoading(false);
          return;
        }

        // Send email verification OTP
        const otpSent = await sendVerificationOTP(data.email);
        if (otpSent) {
          setRegistrationStep(2);
        }
      }

      // Step 2: Email Verification
      else if (registrationStep === 2) {
        if (!otpValue || otpValue.length !== 6) {
          toast.error("Please enter a valid 6-digit verification code");
          setIsLoading(false);
          return;
        }

        const verified = await verifyOTP(data.email, otpValue);
        if (verified) {
          setRegistrationStep(3);
        }
      }
      // Step 3: Payment
      else if (registrationStep === 3) {
        if (!paymentMethod) {
          toast.error("Please select a payment method");
          setIsLoading(false);
          return;
        }

        const paymentId = await processPayment(data);
        if (paymentId) {
          // Create form data for final registration
          const formData = new FormData();
          formData.append("fullName", data.fullName);
          formData.append("email", data.email);
          formData.append("phone", data.phone);
          formData.append("gender", data.gender);
          formData.append("age", data.age);
          formData.append("password", data.password);
          formData.append("studentId", data.studentId);
          formData.append("courseId", selectedCourse);
          formData.append("paymentOptionType", selectedPaymentOption);
          formData.append("isHafiz", isHafiz);
          formData.append("location", location);
          formData.append("paymentId", paymentId);
          formData.append("paymentAmount", coursePrice);
          formData.append(
            "paymentCurrency",
            location === "nigeria" ? "NGN" : "USD"
          );
          // Include riwayatId for Qira'at courses
          if (data.courseId === 3 && data.riwayatId) {
            formData.append("riwayatId", data.riwayatId);
          }

          if (profileImage) {
            formData.append("profileImage", profileImage);
          }

          try {
            // Complete registration with better error handling
            const response = await axios.post(
              `${API_URL}/auth/register`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            if (response.data && response.data.success) {
              // Store token and user data
              localStorage.setItem("token", response.data.token);
              localStorage.setItem(
                "studentInfo",
                JSON.stringify(response.data.student)
              );

              // Get course and payment details for confirmation email
              const course = AVAILABLE_COURSES.find(
                (c) => c.id === selectedCourse
              );
              const paymentOption = course.paymentOptions.find(
                (po) => po.type === selectedPaymentOption
              );

              // Send confirmation email
              await sendConfirmationEmail({
                email: data.email,
                fullName: data.fullName,
                studentId: data.studentId,
                courseName: course.name,
                paymentType: paymentOption.label,
                paymentAmount: `${
                  location === "nigeria" ? "₦" : "$"
                }${coursePrice.toLocaleString()}`,
              });

              // Show success toast
              toast.success(
                "Registration successful! Redirecting to dashboard..."
              );

              // Delay navigation to allow toast to be visible
              setTimeout(() => {
                navigate(studentPath);
              }, 2000);
            } else {
              const errorMessage =
                response.data?.message ||
                "Registration failed. Please try again.";
              toast.error(errorMessage);
            }
          } catch (error) {
            console.error("Registration error:", error);
            if (error.response && error.response.data?.message) {
              toast.error(error.response.data.message);
            } else {
              toast.error("Registration failed. Please try again.");
            }
          }
        }
      }
    } catch (error) {
      console.error("Registration process error:", error);
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Get the selected course details
  const getSelectedCourseDetails = () => {
    return AVAILABLE_COURSES.find((c) => c.id === selectedCourse) || null;
  };

  // Render the form based on current registration step
  const renderRegistrationStep = () => {
    switch (registrationStep) {
      case 1:
        return renderBasicRegistrationForm();
      case 2:
        return renderEmailVerificationForm();
      case 3:
        return renderPaymentForm();
      default:
        return renderBasicRegistrationForm();
    }
  };

  // Step 1: Basic Registration Form
  const renderBasicRegistrationForm = () => {
    const selectedCourseDetails = getSelectedCourseDetails();
    console.log("Selected course details:", selectedCourseDetails);
    return (
      <>
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 border-b pb-2">
            Personal Information
          </h3>

          {/* Profile Picture Upload */}
          <div className="mb-5 flex flex-col items-center">
            <div className="relative w-32 h-32 rounded-full overflow-hidden bg-gray-200 mb-3">
              {previewImage ? (
                <img
                  src={previewImage}
                  alt="Profile Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <FaCamera size={32} />
                </div>
              )}
              <label
                htmlFor="profileImage"
                className="absolute bottom-0 w-full py-1 bg-black bg-opacity-50 text-white text-xs text-center cursor-pointer"
              >
                Upload
              </label>
              <input
                type="file"
                id="profileImage"
                onChange={handleImageChange}
                className="hidden"
                accept="image/jpeg, image/png"
              />
            </div>
            <p className="text-xs text-gray-500">
              Upload your profile picture (optional)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name Field */}
            <div className="space-y-1">
              <div className="flex gap-1">
                <label htmlFor="fullName" className="text-sm text-slate-500">
                  <i>Full Name</i>
                </label>
                <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
              </div>
              <input
                type="text"
                {...register("fullName", {
                  required: "Full name is required",
                  minLength: {
                    value: 3,
                    message: "Name must be at least 3 characters",
                  },
                })}
                placeholder="Full Name"
                id="fullName"
                className="w-full p-2 border rounded-md"
              />
              {errors.fullName && (
                <p className="text-red-600 text-sm">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Student ID Field (Auto-generated and read-only) */}
            <div className="space-y-1">
              <div className="flex gap-1">
                <label htmlFor="studentId" className="text-sm text-slate-500">
                  <i>Student ID (Auto-generated)</i>
                </label>
                <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
              </div>
              <input
                type="text"
                {...register("studentId")}
                value={studentId}
                readOnly
                placeholder="Generating..."
                id="studentId"
                className="w-full p-2 bg-gray-100 border rounded-md cursor-not-allowed"
              />
            </div>

            {/* Email Field */}
            <div className="space-y-1">
              <div className="flex gap-1">
                <label htmlFor="email" className="text-sm text-slate-500">
                  <i>Email</i>
                </label>
                <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
              </div>
              <input
                type="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                placeholder="Email"
                id="email"
                className="w-full p-2 border rounded-md"
              />
              {errors.email && (
                <p className="text-red-600 text-sm">{errors.email.message}</p>
              )}
            </div>

            {/* Phone Field */}
            <div className="space-y-1">
              <div className="flex gap-1">
                <label htmlFor="phone" className="text-sm text-slate-500">
                  <i>Phone Number</i>
                </label>
                <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
              </div>
              <input
                type="tel"
                {...register("phone", {
                  required: "Phone number is required",
                })}
                placeholder="Phone Number"
                id="phone"
                className="w-full p-2 border rounded-md"
              />
              {errors.phone && (
                <p className="text-red-600 text-sm">{errors.phone.message}</p>
              )}
            </div>

            {/* Gender Field */}
            <div className="space-y-1">
              <div className="flex gap-1">
                <label htmlFor="gender" className="text-sm text-slate-500">
                  <i>Gender</i>
                </label>
                <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
              </div>
              <select
                id="gender"
                {...register("gender", { required: "Gender is required" })}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              {errors.gender && (
                <p className="text-red-600 text-sm">{errors.gender.message}</p>
              )}
            </div>

            {/* Age Group Field */}
            <div className="space-y-1">
              <div className="flex gap-1">
                <label htmlFor="age" className="text-sm text-slate-500">
                  <i>Age Group</i>
                </label>
                <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
              </div>
              <select
                id="age"
                {...register("age", { required: "Age group is required" })}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Select age group</option>
                <option value="child">Child (5-12)</option>
                <option value="teen">Teen (13-17)</option>
                <option value="young-adult">Young Adult (18-24)</option>
                <option value="adult">Adult (25+)</option>
              </select>
              {errors.age && (
                <p className="text-red-600 text-sm">{errors.age.message}</p>
              )}
            </div>

            {/* Location Field */}
            <div className="space-y-1">
              <div className="flex gap-1">
                <label htmlFor="location" className="text-sm text-slate-500">
                  <i>Your Location</i>
                </label>
                <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
              </div>
              <select
                id="location"
                value={location}
                {...register("location", { required: "Location is required" })}
                onChange={handleLocationChange}
                className="w-full p-2 border rounded-md"
              >
                <option value="nigeria">Nigeria</option>
                <option value="diaspora">Diaspora (Outside Nigeria)</option>
              </select>
              <p className="text-xs text-gray-500">
                Your location determines the payment currency and pricing
              </p>
            </div>

            {/* Hafiz Status Field */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  id="isHafiz"
                  checked={isHafiz}
                  {...register("isHafiz")}
                  onChange={handleHafizStatusChange}
                  className="w-4 h-4"
                />
                <label htmlFor="isHafiz" className="text-sm">
                  I am a Hafiz (I have memorized the complete Quran)
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Some advanced courses are exclusive to Huffaz
              </p>
            </div>
          </div>
        </div>

        {/* Course Selection Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 border-b pb-2">
            Course Selection
          </h3>
          {/* Use the QiraatCourseSelection component */}
          <QiraatCourseSelection
            isHafiz={isHafiz}
            selectedCourse={selectedCourse}
            handleCourseSelect={handleCourseSelect}
            location={location}
            register={register}
            setValue={setValue}
            errors={errors}
            updateIt={handlePaymentOptionSelect}
          />
          {/* Password Field */}
          <div className="relative space-y-2 mb-5">
            <div className="flex gap-1">
              <label htmlFor="password" className="text-sm text-slate-500">
                <i>Password</i>
              </label>
              <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
                pattern: {
                  value:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                  message:
                    "Password must include uppercase, lowercase, number and special character",
                },
              })}
              placeholder="Password"
              id="password"
              className="bg-[#cedce1] p-2 rounded border-none outline-none w-full"
            />
            <div
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 bottom-3 cursor-pointer"
            >
              {showPassword ? (
                <FaEyeSlash className="text-sm" />
              ) : (
                <FaEye className="text-sm" />
              )}
            </div>
            {errors.password && (
              <p className="text-red-600 text-sm">{errors.password.message}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="relative space-y-2">
            <div className="flex gap-1">
              <label
                htmlFor="confirmPassword"
                className="text-sm text-slate-500"
              >
                <i>Confirm Password</i>
              </label>
              <FaStarOfLife className="text-[7px] mt-1 text-red-500" />
            </div>
            <input
              type={showConfirmPassword ? "text" : "password"}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === password || "Passwords do not match",
              })}
              placeholder="Confirm Password"
              id="confirmPassword"
              className="bg-[#cedce1] p-2 rounded border-none outline-none w-full"
            />
            <div
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 bottom-3 cursor-pointer"
            >
              {showConfirmPassword ? (
                <FaEyeSlash className="text-sm" />
              ) : (
                <FaEye className="text-sm" />
              )}
            </div>
            {errors.confirmPassword && (
              <p className="text-red-600 text-sm">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="mt-8 space-x-1">
            <input
              type="checkbox"
              id="terms"
              {...register("terms", {
                required: "You must agree to the terms and conditions",
              })}
            />
            <label htmlFor="terms">I agree to the Terms and Conditions</label>
            {errors.terms && (
              <p className="text-red-600 text-sm">{errors.terms.message}</p>
            )}
          </div>
        </div>
      </>
    );
  };

  // Step 2: Email Verification Form
  const renderEmailVerificationForm = () => {
    return (
      <div className="py-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold">Email Verification</h3>
          <p className="text-gray-600 mt-2">
            We've sent a verification code to your email. Please enter it below
            to verify your account.
          </p>
        </div>

        <div className="mb-8">
          <label
            htmlFor="otp"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Verification Code
          </label>
          <input
            type="text"
            id="otp"
            value={otpValue}
            onChange={handleOtpChange}
            placeholder="Enter 6-digit code"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={6}
          />
        </div>

        <div className="text-center text-sm text-gray-600">
          <p>
            Didn't receive the code?{" "}
            <button
              type="button"
              className="text-blue-600 font-medium"
              onClick={() => sendVerificationOTP(watch("email"))}
            >
              Resend Code
            </button>
          </p>
        </div>
      </div>
    );
  };

  // Step 3: Payment Form
  const renderPaymentForm = () => {
    const selectedCourseDetails = getSelectedCourseDetails();
    const formValues = watch(); // Get all form values
    // Special handling for Qira'at courses with riwayat selection
    let paymentOption;
    let courseName = selectedCourseDetails?.name || "";
    let currentPrice = 0;

    if (selectedCourse === 3 && formValues.riwayatId) {
      // For Qira'at courses, get the riwayat data
      try {
        const riwayatId = parseInt(formValues.riwayatId);
        const riwayatData = QIRAAT_RIWAYAT.find((r) => r.id === riwayatId);

        if (riwayatData) {
          // Append riwayat name to course name
          courseName = `${selectedCourseDetails?.name} - ${riwayatData.name}`;

          // Get the specific payment option for this riwayat
          paymentOption = riwayatData.paymentOptions.find(
            (po) => po.type === formValues.paymentOptionType
          );
          // Set price based on location
          if (paymentOption) {
            currentPrice =
              location === "nigeria"
                ? paymentOption.nigeriaPrice
                : paymentOption.diasporaPrice;
          }
        }
      } catch (error) {
        console.error("Error processing riwayat data:", error);
      }
    } else if (selectedCourseDetails) {
      // For regular courses
      paymentOption = selectedCourseDetails.paymentOptions.find(
        (po) => po.type === formValues.paymentOptionType
      );
      // Set price based on location
      if (paymentOption) {
        currentPrice =
          location === "nigeria"
            ? paymentOption.nigeriaPrice
            : paymentOption.diasporaPrice;
      }
    }
    // Ensure we have a valid price - either use the stored coursePrice or calculate it
    // Fallback to coursePrice form value if calculated price is 0
    if (currentPrice === 0 && formValues.coursePrice) {
      currentPrice = formValues.coursePrice;
    }

    return (
      <div className="py-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold">Complete Your Registration</h3>
          <p className="text-gray-600 mt-2">
            Please complete the payment to finalize your registration.
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="font-medium text-lg mb-2">Order Summary</h4>
          <div className="flex justify-between py-2 border-b">
            <span>Course:</span>
            <span className="font-medium">{courseName}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span>Payment Plan:</span>
            <span className="font-medium">
              {paymentOption?.label ||
                (watch("paymentOptionType") === "monthly"
                  ? "Monthly Plan"
                  : watch("paymentOptionType") === "full"
                  ? "Full Course"
                  : "N/A")}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span>Student ID:</span>
            <span className="font-medium">{studentId || "Generating..."}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span>Location:</span>
            <span className="font-medium capitalize">{location}</span>
          </div>
          <div className="flex justify-between py-2 text-lg font-bold">
            <span>Total Amount:</span>
            <span className="text-green-600">
              {location === "nigeria" ? "₦" : "$"}
              {currentPrice.toLocaleString()}
              {currentPrice > 0 ? currentPrice.toLocaleString() : "0"}
              {watch("paymentOptionType") === "monthly" && (
                <span className="text-sm font-normal">/month</span>
              )}
            </span>
          </div>
        </div>
        <div className="mb-6">
          <h4 className="font-medium mb-3">Select Payment Method</h4>
          <div className="space-y-3">
            <div
              className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
              onClick={() => setPaymentMethod("card")}
            >
              <input
                type="radio"
                id="card"
                name="paymentMethod"
                value="card"
                checked={paymentMethod === "card"}
                onChange={handlePaymentMethodChange}
              />
              <label htmlFor="card" className="flex-1 cursor-pointer">
                Credit/Debit Card
              </label>
            </div>

            {location === "nigeria" && (
              <>
                <div
                  className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                  onClick={() => setPaymentMethod("transfer")}
                >
                  <input
                    type="radio"
                    id="transfer"
                    name="paymentMethod"
                    value="transfer"
                    checked={paymentMethod === "transfer"}
                    onChange={handlePaymentMethodChange}
                  />
                  <label htmlFor="transfer" className="flex-1 cursor-pointer">
                    Bank Transfer
                  </label>
                </div>

                <div
                  className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                  onClick={() => setPaymentMethod("ussd")}
                >
                  <input
                    type="radio"
                    id="ussd"
                    name="paymentMethod"
                    value="ussd"
                    checked={paymentMethod === "ussd"}
                    onChange={handlePaymentMethodChange}
                  />
                  <label htmlFor="ussd" className="flex-1 cursor-pointer">
                    USSD
                  </label>
                </div>
              </>
            )}

            <div
              className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
              onClick={() => setPaymentMethod("paypal")}
            >
              <input
                type="radio"
                id="paypal"
                name="paymentMethod"
                value="paypal"
                checked={paymentMethod === "paypal"}
                onChange={handlePaymentMethodChange}
              />
              <label htmlFor="paypal" className="flex-1 cursor-pointer">
                PayPal
              </label>
              {location !== "nigeria" && (
                <span className="text-xs text-gray-500 ml-2">
                  (Recommended for international students)
                </span>
              )}
            </div>
          </div>
          {!paymentMethod && (
            <p className="text-red-600 text-sm mt-2">
              Please select a payment method
            </p>
          )}
        </div>

        {/* Dynamic Payment Form based on selected method */}
        {paymentMethod === "card" && (
          <div className="space-y-4">
            <div>
              <label htmlFor="cardNumber" className="block text-sm mb-1">
                Card Number
              </label>
              <input
                type="text"
                id="cardNumber"
                placeholder="1234 5678 9012 3456"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiryDate" className="block text-sm mb-1">
                  Expiry Date
                </label>
                <input
                  type="text"
                  id="expiryDate"
                  placeholder="MM/YY"
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label htmlFor="cvv" className="block text-sm mb-1">
                  CVV
                </label>
                <input
                  type="text"
                  id="cvv"
                  placeholder="123"
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
            </div>
            <div>
              <label htmlFor="cardName" className="block text-sm mb-1">
                Name on Card
              </label>
              <input
                type="text"
                id="cardName"
                placeholder="Full Name"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
          </div>
        )}

        {paymentMethod === "transfer" && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h5 className="font-medium mb-2">Bank Account Details</h5>
            <div className="text-sm space-y-2">
              <p>
                <span className="font-medium">Bank Name:</span> Providus Bank
              </p>
              <p>
                <span className="font-medium">Account Name:</span> Muqrahat ul
                Tanzeel Global Quranic Academy
              </p>
              <p>
                <span className="font-medium">Account Number:</span> 0123456789
              </p>
              <p>
                <span className="font-medium">Reference:</span> {studentId}
              </p>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p>
                Please make your payment using the details above. Include your
                Student ID as reference.
              </p>
              <p className="mt-2">
                After payment, click "Complete Registration" to proceed.
              </p>
            </div>
          </div>
        )}

        {paymentMethod === "paypal" && (
          <div className="text-center p-4">
            <p className="mb-4">
              Click the button below to complete payment via PayPal
            </p>
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-8 rounded font-medium"
            >
              Pay with PayPal
            </button>
            <p className="mt-4 text-sm text-gray-600">
              You'll be redirected to PayPal to complete your payment
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex justify-center items-center sm:max:h-[80vh] mb-10 px-2">
      <div className="bg-white shadow-2xl rounded-xl gap-7 p-10 flex flex-col w-[40rem]">
        <ToastContainer position="top-right" autoClose={5000} />
        <h2 className="font-bold text-2xl">SIGN UP</h2>
        <p className="text-xs">Create your student account</p>

        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full ${
                registrationStep >= 1
                  ? "bg-green-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              1
            </div>
            <div className="text-xs ml-1">Account</div>
          </div>
          <div className="h-0.5 w-12 bg-gray-200 mx-2"></div>
          <div className="flex items-center">
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full ${
                registrationStep >= 2
                  ? "bg-green-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              2
            </div>
            <div className="text-xs ml-1">Verify</div>
          </div>
          <div className="h-0.5 w-12 bg-gray-200 mx-2"></div>
          <div className="flex items-center">
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full ${
                registrationStep >= 3
                  ? "bg-green-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              3
            </div>
            <div className="text-xs ml-1">Payment</div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {renderRegistrationStep()}

          {/* Action Buttons */}
          <div className="flex justify-between mt-6">
            {registrationStep > 1 && (
              <Button
                btnBgColor="bg-gray-300"
                btnColor="text-gray-800"
                padding="py-3 px-6"
                btnText="Back"
                type="button"
                onClick={() => setRegistrationStep(registrationStep - 1)}
              />
            )}

            <Button
              btnBgColor="bg-[#3b76db]"
              btnColor="text-white"
              padding={`py-3 ${registrationStep > 1 ? "px-6" : "w-full"}`}
              btnText={
                isLoading
                  ? "Processing..."
                  : registrationStep === 1
                  ? "Continue to Verification"
                  : registrationStep === 2
                  ? "Verify & Continue"
                  : "Complete Registration"
              }
              type="submit"
              disabled={
                isLoading ||
                (registrationStep === 1 && !selectedCourse) ||
                (registrationStep === 2 && !otpValue) ||
                (registrationStep === 3 && !paymentMethod)
              }
              className={registrationStep === 1 ? "w-full" : "ml-auto"}
            />
          </div>
        </form>

        {/* Links */}
        <div className="text-center text-sm -mt-2">
          <div className="flex justify-center gap-1">
            <p>Already have an account?</p>
            <Link
              to={loginPath}
              className="font-semibold text-blue-600 hover:underline"
            >
              Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;
